import { useEffect, useRef, useState, type ComponentType, type SVGProps } from "react";
import { Popover } from "radix-ui";
import type { Post, PostReaction } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { compact } from "../../lib/format";
import { usePrefs } from "../../lib/prefs";
import { Tooltip } from "../../components/ui/Tooltip";
import { useReducedMotion } from "../../components/ui/Avatar";
import { IconHeart } from "../../components/icons";
import { IconReactFire, IconReactHaha, IconReactLike, IconReactLove, IconReactSad, IconReactWow } from "../../components/socialIcons";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export const REACTIONS: { kind: PostReaction; label: string; icon: Icon; tone: string; anim: string }[] = [
  { kind: "like", label: "Like", icon: IconReactLike, tone: "react-like", anim: "anim-pop" },
  { kind: "love", label: "Love", icon: IconReactLove, tone: "react-love", anim: "anim-pop" },
  { kind: "haha", label: "Haha", icon: IconReactHaha, tone: "react-haha", anim: "anim-haha" },
  { kind: "wow", label: "Wow", icon: IconReactWow, tone: "react-wow", anim: "anim-wow" },
  { kind: "sad", label: "Sad", icon: IconReactSad, tone: "react-sad", anim: "anim-sad" },
  { kind: "fire", label: "Fire", icon: IconReactFire, tone: "react-fire", anim: "anim-fire" },
];
export const reactionMeta = (k: PostReaction | null | undefined) => REACTIONS.find((r) => r.kind === k) ?? REACTIONS[0];

/** Whether celebratory motion should play: the account preference and reduced motion both apply. */
export function useCelebrate() {
  const prefs = usePrefs();
  const reduced = useReducedMotion();
  return { reactions: prefs.reactionAnimations && !reduced, particles: prefs.particleEffects && prefs.reactionAnimations && !reduced };
}

export function haptic() {
  try {
    navigator.vibrate?.(10);
  } catch {
    /* unsupported */
  }
}

/**
 * A number that rolls digit by digit when it changes. Tabular figures keep
 * the width steady; screen readers get the plain value.
 */
export function RollingCount({ value, className }: { value: number; className?: string }) {
  const { reactions } = useCelebrate();
  const text = compact(value);
  if (!reactions || !/^\d+$/.test(text)) return <span className={cn("tabular", className)}>{text}</span>;
  return (
    <span className={cn("digit-roll tabular", className)} aria-label={text}>
      {text.split("").map((ch, i) => (
        <span key={text.length - i} aria-hidden className="relative">
          <span className="digit-col" style={{ transform: `translateY(-${Number(ch) * 10}%)` }}>
            {Array.from({ length: 10 }, (_, d) => (
              <span key={d} className="block h-[1.25em]">
                {d}
              </span>
            ))}
          </span>
        </span>
      ))}
    </span>
  );
}

/** Six small dots flying out of the button. Re-mounts on each `seed`. */
function Burst({ seed }: { seed: number }) {
  if (!seed) return null;
  return (
    <span key={seed} className="burst" aria-hidden>
      {Array.from({ length: 7 }, (_, i) => (
        <span key={i} style={{ ["--a" as string]: `${i * (360 / 7) + (seed % 30)}deg`, animationDelay: `${i * 12}ms` }} />
      ))}
    </span>
  );
}

const LONG_PRESS_MS = 380;

/**
 * The like control. Tapping toggles a plain like (or removes any reaction);
 * holding, or hovering on desktop, opens the reaction picker. Each reaction
 * has its own short animation; the like heart also gets a small particle
 * burst. Everything respects the motion preferences.
 */
export function ReactionButton({ post, onReact, showCount }: { post: Post; onReact: (kind: PostReaction | null) => void; showCount: boolean }) {
  const [open, setOpen] = useState(false);
  const [anim, setAnim] = useState<string | null>(null);
  const [burst, setBurst] = useState(0);
  const press = useRef<{ timer: ReturnType<typeof setTimeout> | null; long: boolean }>({ timer: null, long: false });
  const hover = useRef<ReturnType<typeof setTimeout> | null>(null);
  const celebrate = useCelebrate();
  const meta = post.liked ? reactionMeta(post.myReaction) : null;
  const I = meta?.icon ?? IconHeart;

  useEffect(() => () => {
    if (press.current.timer) clearTimeout(press.current.timer);
    if (hover.current) clearTimeout(hover.current);
  }, []);

  const play = (kind: PostReaction | null) => {
    if (!celebrate.reactions) return;
    const m = kind ? reactionMeta(kind) : null;
    setAnim(null);
    requestAnimationFrame(() => setAnim(m ? m.anim : "anim-unpop"));
    if (kind && celebrate.particles) setBurst((b) => b + 1);
  };

  const choose = (kind: PostReaction | null) => {
    setOpen(false);
    if (kind) haptic();
    play(kind);
    onReact(kind);
  };

  const label = post.liked ? `Remove ${meta!.label} reaction` : "Like";
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <span
          className="relative inline-flex"
          onPointerEnter={(e) => {
            if (e.pointerType !== "mouse") return;
            hover.current = setTimeout(() => setOpen(true), 550);
          }}
          onPointerLeave={() => hover.current && clearTimeout(hover.current)}
        >
          <Tooltip label={open ? null : post.liked ? meta!.label : "Like"} disabled={open}>
            <button
              type="button"
              aria-label={showCount && post.likeCount > 0 ? `${label}, ${post.likeCount}` : label}
              aria-pressed={post.liked}
              aria-haspopup="menu"
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                press.current.long = false;
                press.current.timer = setTimeout(() => {
                  press.current.long = true;
                  haptic();
                  setOpen(true);
                }, LONG_PRESS_MS);
              }}
              onPointerUp={() => press.current.timer && clearTimeout(press.current.timer)}
              onPointerCancel={() => press.current.timer && clearTimeout(press.current.timer)}
              onContextMenu={(e) => {
                // Long-press on touch opens the picker instead of the system menu.
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (press.current.long) return;
                if (hover.current) clearTimeout(hover.current);
                choose(post.liked ? null : "like");
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || (e.key === "Enter" && e.shiftKey)) {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
              className={cn(
                "relative inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-fg-3 transition-colors duration-150 hover:bg-surface-2",
                meta ? meta.tone : "hover:text-like",
              )}
            >
              <span className="relative inline-flex">
                <I className={cn("icon size-[18px]", anim)} aria-hidden onAnimationEnd={() => setAnim(null)} />
                <Burst seed={burst} />
              </span>
              {showCount && post.likeCount > 0 && <RollingCount value={post.likeCount} />}
            </button>
          </Tooltip>
        </span>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onClick={(e) => e.stopPropagation()}
          onPointerLeave={(e) => e.pointerType === "mouse" && setOpen(false)}
          className="reaction-picker z-[60] flex items-center gap-0.5 rounded-full bg-surface p-1 shadow-pop animate-pop-in"
          role="menu"
          aria-label="Reactions"
        >
          {REACTIONS.map((r) => {
            const active = post.liked && post.myReaction === r.kind;
            return (
              <Tooltip key={r.kind} label={r.label}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  aria-label={r.label}
                  onClick={() => choose(active ? null : r.kind)}
                  className={cn("flex size-9 items-center justify-center rounded-full", r.tone, active ? "bg-surface-3" : "hover:bg-surface-2")}
                >
                  <r.icon className="icon size-[22px]" aria-hidden />
                </button>
              </Tooltip>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Small stacked icons of the most used reactions, shown beside the count on the post page. */
export function TopReactions({ kinds }: { kinds: PostReaction[] }) {
  if (!kinds.length) return null;
  return (
    <span className="inline-flex items-center -space-x-1" aria-label={`Reactions: ${kinds.map((k) => reactionMeta(k).label).join(", ")}`}>
      {kinds.map((k) => {
        const m = reactionMeta(k);
        return (
          <span key={k} className={cn("inline-flex size-5 items-center justify-center rounded-full bg-surface-2 ring-2 ring-bg", m.tone)}>
            <m.icon className="size-3.5" aria-hidden />
          </span>
        );
      })}
    </span>
  );
}

/** The centred heart for double-tap / double-click to like. */
export function DoubleTapHeart({ seed }: { seed: number }) {
  if (!seed) return null;
  return <IconReactLike key={seed} className="dbl-heart size-20" aria-hidden />;
}
